import { supabase } from './supabaseClient.js';
import { processBook } from './llmService.js';
import { checkDatabaseTables } from './databaseSetup.js';
import { checkAndFixDatabase } from './fixDatabase.js';

export async function simpleBookProcessor(file) {
  console.log('🚀 [SIMPLE PROCESSOR] Starting book processing for:', file.name);
  console.log('⚡ Enhanced debugging enabled - timestamp:', new Date().toISOString());
  console.log('🔧 [DEBUG] Environment check:', {
    hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
    hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_KEY,
    isProduction: import.meta.env.PROD
  });
  let createdBookId = null; // Track created book ID for robust cleanup on error
  // Check database operations first with detailed error catching
  try {
    console.log('🔍 [DEBUG] Checking database operations...');
    const dbWorks = await checkAndFixDatabase();
    if (!dbWorks) {
      throw new Error('Database operations are not working. Please check your Supabase configuration.');
    }
    console.log('✅ [DEBUG] Database check passed');
    
    // In development, avoid strict table validation.
    // The pipeline will adapt to available tables (e.g., skip segments if table is missing).
    console.log('🔍 [DEBUG] Skipping strict table validation; pipeline will adapt to available tables');
    
  } catch (dbCheckError) {
    console.error('❌ [DEBUG] Database check failed:', dbCheckError);
    console.error('❌ [DEBUG] Full error object:', JSON.stringify(dbCheckError, null, 2));
    throw new Error(`Database check failed: ${dbCheckError.message}`);
  }
  
  try {
    // Step 1: Upload file to storage with detailed error handling
    console.log('📤 [SIMPLE PROCESSOR] Step 1: Uploading file to storage...');
    const filePath = `${Date.now()}_${file.name}`;
    console.log('🔧 [DEBUG] Upload details:', {
      filePath,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    let storageResult;
    try {
      storageResult = await supabase.storage
        .from("books")
        .upload(filePath, file, { upsert: true });
      
      console.log('🔧 [DEBUG] Storage upload raw result:', JSON.stringify(storageResult, null, 2));
    } catch (storageException) {
      console.error('❌ [DEBUG] Storage upload exception:', storageException);
      console.error('❌ [DEBUG] Exception type:', typeof storageException);
      console.error('❌ [DEBUG] Exception string:', String(storageException));
      throw new Error(`Storage upload exception: ${storageException.message || String(storageException)}`);
    }
    
    const { data: storageData, error: storageError } = storageResult;
    
    if (storageError) {
      console.error('❌ [DEBUG] Storage error details:', JSON.stringify(storageError, null, 2));
      throw new Error(`Storage upload failed: ${storageError.message}`);
    }
    console.log('✅ [SIMPLE PROCESSOR] File uploaded successfully');
    console.log('🔧 [DEBUG] Storage data:', JSON.stringify(storageData, null, 2));
    
    // Step 2: Get public URL with error handling
    console.log('🔧 [DEBUG] Getting public URL...');
    let publicUrlResult;
    try {
      publicUrlResult = supabase.storage.from("books").getPublicUrl(filePath);
      console.log('🔧 [DEBUG] Public URL result:', JSON.stringify(publicUrlResult, null, 2));
    } catch (urlException) {
      console.error('❌ [DEBUG] Public URL exception:', urlException);
      throw new Error(`Public URL generation failed: ${urlException.message || String(urlException)}`);
    }
    
    const { data: publicUrlData } = publicUrlResult;
    
    // Step 3: Create book record with detailed error handling
    console.log('📝 [SIMPLE PROCESSOR] Step 2: Creating book record...');
    const bookRecord = {
      title: file.name,
      file_url: publicUrlData.publicUrl,
      status: 'processing',
    };
    console.log('🔧 [DEBUG] Book record to insert:', JSON.stringify(bookRecord, null, 2));
    
    let dbResult;
    try {
      dbResult = await supabase.from("books").insert([bookRecord]).select();
      console.log('🔧 [DEBUG] Database insert raw result:', JSON.stringify(dbResult, null, 2));
    } catch (dbException) {
      console.error('❌ [DEBUG] Database insert exception:', dbException);
      console.error('❌ [DEBUG] Exception type:', typeof dbException);
      console.error('❌ [DEBUG] Exception string:', String(dbException));
      throw new Error(`Database insert exception: ${dbException.message || String(dbException)}`);
    }
    
    const { data: bookData, error: dbError } = dbResult;
    
    if (dbError) {
      console.error('❌ [DEBUG] Database error details:', JSON.stringify(dbError, null, 2));
      throw new Error(`Database error: ${dbError.message}`);
    }
    
    if (!bookData || bookData.length === 0) {
      console.error('❌ [DEBUG] No book data returned from insert');
      throw new Error('Database insert succeeded but returned no data');
    }
    
    const bookId = bookData[0].id;
    createdBookId = bookId;
    console.log('✅ [SIMPLE PROCESSOR] Book record created with ID:', bookId);
    console.log('🔧 [DEBUG] Full book data:', JSON.stringify(bookData[0], null, 2));
    
    // Step 4: Process the book (this is the main work)
    console.log('🔧 [SIMPLE PROCESSOR] Step 3: Processing book content...');
    let processedData;
    try {
      // Try enhanced parsing pipeline if available
      try {
        console.log('🔧 [SIMPLE PROCESSOR] Attempting enhanced parsing pipeline...');
        const { parseBookPipeline } = await import('./fileParser.js');
        console.log('🔧 [SIMPLE PROCESSOR] Enhanced parsing module loaded successfully');
        
        const pipelineResult = await parseBookPipeline(file, bookId);
        console.log('🔧 [SIMPLE PROCESSOR] Pipeline result:', {
          chaptersCount: pipelineResult.chaptersCount,
          totalSegments: pipelineResult.totalSegments,
          bookId: pipelineResult.bookId
        });
        
        // Convert pipeline result to legacy format for compatibility
        processedData = {
          summary: `Book parsed with ${pipelineResult.chaptersCount} chapters and ${pipelineResult.totalSegments} segments`,
          metadata: {
            genre: 'General',
            characters: [{ name: 'Narrator', gender: 'unknown', country: 'US' }],
            plot_moments: []
          },
          chapters: pipelineResult.chapters
        };
        console.log('✅ [SIMPLE PROCESSOR] Enhanced parsing pipeline completed successfully');
      } catch (pipelineError) {
        console.error('❌ [SIMPLE PROCESSOR] Enhanced pipeline failed with full error:', pipelineError);
        console.error('❌ [SIMPLE PROCESSOR] Error stack:', pipelineError.stack);
        console.warn('⚠️ [SIMPLE PROCESSOR] Falling back to legacy processing...');
        
        try {
          processedData = await processBook(file, bookId);
          console.log('✅ [SIMPLE PROCESSOR] Legacy processing completed successfully');
        } catch (legacyError) {
          console.error('❌ [SIMPLE PROCESSOR] Legacy processing also failed:', legacyError);
          console.error('❌ [SIMPLE PROCESSOR] Legacy error stack:', legacyError.stack);
          throw new Error(`Both enhanced and legacy processing failed. Enhanced: ${pipelineError.message}, Legacy: ${legacyError.message}`);
        }
      }
      
      console.log('✅ [SIMPLE PROCESSOR] Book content processed successfully');
      console.log('🔧 [DEBUG] Processed data structure:', {
        hasChapters: !!processedData.chapters,
        chaptersCount: processedData.chapters?.length || 0,
        hasMetadata: !!processedData.metadata,
        hasSummary: !!processedData.summary
      });
    } catch (processError) {
      console.error('❌ [DEBUG] Process book error:', processError);
      console.error('❌ [DEBUG] Process error type:', typeof processError);
      console.error('❌ [DEBUG] Process error string:', String(processError));
      
      // Check if this looks like a JSON parsing error
      if (processError.message && processError.message.includes('Unexpected token')) {
        console.error('🔍 [DEBUG] This looks like a JSON parsing error!');
        console.error('🔍 [DEBUG] Likely receiving HTML instead of JSON from an API call');
      }
      
      throw new Error(`Book processing failed: ${processError.message || String(processError)}`);
    }
    
    // Step 5: Save all the processed data
    console.log('💾 [SIMPLE PROCESSOR] Step 4: Saving processed data...');
    
    // Save metadata
    const { data: metadataData, error: metadataError } = await supabase.from("book_metadata").insert([
      {
        book_id: bookId,
        summary: processedData.summary,
        genre: processedData.metadata.genre,
      },
    ]).select();
    
    if (metadataError) {
      console.error('⚠️ [SIMPLE PROCESSOR] Metadata save warning:', metadataError);
    } else {
      const metadataId = metadataData[0].id;
      console.log('✅ [SIMPLE PROCESSOR] Metadata saved with ID:', metadataId);
      
      // Save characters if available
      if (processedData.metadata.characters.length > 0) {
        console.log('📝 [SIMPLE PROCESSOR] Characters found:', processedData.metadata.characters.length);
      }
      
      // Save plot moments
      if (processedData.metadata.plot_moments.length > 0) {
        try {
          const momentsToInsert = processedData.metadata.plot_moments.map(moment => ({
            book_metadata_id: metadataId,
            moment_summary: moment.summary,
          }));
          
          const { error: momentError } = await supabase.from("plot_moments").insert(momentsToInsert);
          if (momentError) {
            console.error('⚠️ [SIMPLE PROCESSOR] Plot moments save error:', momentError);
          } else {
            console.log('✅ [SIMPLE PROCESSOR] Plot moments saved');
          }
        } catch (momentError) {
          console.error('⚠️ [SIMPLE PROCESSOR] Plot moments save exception:', momentError);
        }
      }
    }
    
    // Save chapters
    if (processedData.chapters.length > 0) {
      try {
        const chaptersToInsert = processedData.chapters.map(chapter => ({
          book_id: bookId,
          title: chapter.title,
          order: chapter.order,
          text: chapter.text,
        }));
        
        const { error: chapterError } = await supabase.from("chapters").insert(chaptersToInsert);
        if (chapterError) {
          console.error('⚠️ [SIMPLE PROCESSOR] Chapters save error:', chapterError);
        } else {
          console.log('✅ [SIMPLE PROCESSOR] Chapters saved');
        }
      } catch (chapterError) {
        console.error('⚠️ [SIMPLE PROCESSOR] Chapters save exception:', chapterError);
      }
    }
    
    // Step 6: Mark as completed
    console.log('✅ [SIMPLE PROCESSOR] Step 5: Marking book as completed...');
    
    // First check current status
    const { data: currentBook, error: fetchError } = await supabase.from("books").select("status").eq('id', bookId).single();
    if (!fetchError && currentBook) {
      console.log('🔍 [SIMPLE PROCESSOR] Current book status:', currentBook.status);
    }
    
    const { error: updateError } = await supabase
      .from("books")
      .update({ status: 'ready' })
      .eq('id', bookId);
    
    if (updateError) {
      console.error('❌ [SIMPLE PROCESSOR] Failed to mark book as ready:', updateError);
      throw new Error(`Failed to update book status: ${updateError.message}`);
    } else {
      console.log('✅ [SIMPLE PROCESSOR] Book status updated to ready successfully');
    }
    
    console.log('🎉 [SIMPLE PROCESSOR] Book processing completed successfully!');
    return { success: true, bookId };
    
  } catch (error) {
    console.error('❌ [SIMPLE PROCESSOR] Processing failed:', error);
    console.error('❌ [SIMPLE PROCESSOR] Error stack:', error.stack);
    console.error('❌ [SIMPLE PROCESSOR] Error type:', typeof error);
    console.error('❌ [SIMPLE PROCESSOR] Error name:', error.name);
    console.error('❌ [SIMPLE PROCESSOR] Error message:', error.message);
    
    // Check for JSON parsing errors specifically
    if (error.message && error.message.includes('Unexpected token')) {
      console.error('🔍 [DEBUG] JSON PARSING ERROR DETECTED!');
      console.error('🔍 [DEBUG] This means a Supabase API call returned HTML instead of JSON');
      console.error('🔍 [DEBUG] Common causes:');
      console.error('🔍 [DEBUG] - Storage bucket "books" does not exist');
      console.error('🔍 [DEBUG] - Database table "books" does not exist or has wrong schema');
      console.error('🔍 [DEBUG] - Row Level Security (RLS) is blocking operations');
      console.error('🔍 [DEBUG] - Supabase URL/Key mismatch');
      console.error('🔍 [DEBUG] - CORS issues');
      
      // Try to get more details about what's available
      try {
        console.log('🔍 [DEBUG] Attempting to test basic Supabase connection...');
        const testResult = await supabase.from('books').select('count', { count: 'exact', head: true });
        console.log('🔍 [DEBUG] Test query result:', JSON.stringify(testResult, null, 2));
      } catch (testError) {
        console.error('🔍 [DEBUG] Test query also failed:', testError);
      }
    }
    
    // If we have a bookId, mark it as error
    const idToMark = error.bookId || createdBookId;
    if (idToMark) {
      try {
        await supabase
          .from("books")
          .update({ status: 'error' })
          .eq('id', idToMark);
        console.log('📝 [SIMPLE PROCESSOR] Marked book as error');
      } catch (updateError) {
        console.error('❌ [SIMPLE PROCESSOR] Failed to mark book as error:', updateError);
      }
    }
    
    throw error;
  }
} 